// src/hooks/useLoadSocialAccounts.js
import { useDispatch, useSelector } from 'react-redux';
import { useHttpWithCache } from 'hooks/useHtthWithCache'; // same helper you use elsewhere
import { getAllSocialAccounts } from 'api/socialMedia/socialMedia';

// user reducer listens to this string
const SET_BRANDS = 'SET_BRANDS';

const idOf = (x) => (x && (x._id || x.id)) || x;

function accountsToBrandsTree(raw) {
  const accounts = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw?.accounts)
    ? raw.accounts
    : [];

  const byBrand = new Map();

  for (const acc0 of accounts) {
    if (!acc0) continue;
    const acc = { ...acc0 };
    const brandId = String(acc.brand_id || acc.brandId || 'unknown');

    const brand = byBrand.get(brandId) || {
      _id: brandId,
      brandSettings: { socialAccounts: [], socialTargets: [] },
    };

    const platform = String(acc.platform || '').toLowerCase();
    const record = {
      ...acc,
      _id: String(acc._id || acc.id),
      platform,
      status:
        acc.status ??
        ((acc.accessToken ||
          acc.refreshToken ||
          (Array.isArray(acc.pages) && acc.pages.length > 0))
          ? 'connected'
          : 'disconnected'),
    };

    // upsert account
    const accs = brand.brandSettings.socialAccounts;
    const i = accs.findIndex(a => String(a._id) === record._id);
    if (i === -1) accs.push(record); else accs[i] = { ...accs[i], ...record };

    // default target
    if (acc?.defaultTarget?.id) {
      const composedId = `${platform}:${acc.defaultTarget.id}`;
      const dt = {
        _id: composedId,
        platform,
        externalId: String(acc.defaultTarget.id),
        name: acc.defaultTarget.name,
        type: acc.defaultTarget.type,
        avatarUrl: acc.defaultTarget.avatarUrl,
        isDefault: true,
        account: record._id,
      };
      const tIdx = brand.brandSettings.socialTargets.findIndex(t => String(t._id) === composedId);
      if (tIdx === -1) brand.brandSettings.socialTargets.push(dt);
      else brand.brandSettings.socialTargets[tIdx] = { ...brand.brandSettings.socialTargets[tIdx], ...dt };
    }

    // extra targets
    (acc?.targets || []).forEach(t => {
      const external = String(t?.externalId || t?.id || '');
      if (!external) return;
      const tid = `${platform}:${external}`;
      const nt = { ...t, _id: tid, platform, account: record._id };
      const tIdx = brand.brandSettings.socialTargets.findIndex(x => String(x._id) === tid);
      if (tIdx === -1) brand.brandSettings.socialTargets.push(nt);
      else brand.brandSettings.socialTargets[tIdx] = { ...brand.brandSettings.socialTargets[tIdx], ...nt };
    });

    byBrand.set(brandId, brand);
  }

  return Array.from(byBrand.values());
}


/**
 * Usage:
 *   const { error, loading, request, refresh } = useLoadSocialAccounts();
 *   useEffect(() => { request(); }, [request]);
 */
// local constants mirror your reducer
const PATCH_BRAND_SETTINGS = 'PATCH_BRAND_SETTINGS';

export function useLoadSocialAccounts() {
  const dispatch = useDispatch();
  const brandsRedux = useSelector(s => s.user.brands);

  const { error, loading, request, refresh } = useHttpWithCache({
    requestCallback: async () => {
      const resp = await getAllSocialAccounts(); // might be array or {data}/ {accounts}
      return resp;
    },
    reduxSetter: (resp) => {
      const grouped = accountsToBrandsTree(resp);
      if (!grouped.length) return;

      grouped.forEach(({ _id, brandSettings }) => {
        // merge socialAccounts & socialTargets into existing brand
        dispatch({
          type: PATCH_BRAND_SETTINGS,
          payload: {
            brandId: _id,
            patch: {
              socialAccounts: brandSettings.socialAccounts || [],
              socialTargets: brandSettings.socialTargets || [],
            },
          },
        });
      });
    },
    reduxReset: () => {},
    reduxCash: { brands: brandsRedux },
    allowToCash: true,
  });

  return { error, loading, request, refresh };
}
